import React, { useEffect } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";

import DocumentStatusForm, {
  IDocumentChangeStatusForm,
} from "~/components/Forms/DocumentStatusForm";
import Preloader from "~/components/Preloader";
import { DocumentStatus } from "~/components/Forms/CreateDocument/utils/utils";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  dataToRequest_SetStatus,
  userHaveAccessToDocumentEdit,
} from "~/pages/Documents/utils/utils";
import {
  useGetDocumentLazyQuery,
  useGetProfileLazyQuery,
  useSetDocumentStatusMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePermissions } from "~/shared/context/PermissionContext";

const documentStatusFormId = "DocumentStatusForm";

interface IDocumentStatusModal {
  reFetchTable?: () => void;
}

const DocumentStatusModal = ({ reFetchTable }: IDocumentStatusModal) => {
  const setDataReplace = useSetLocationData(true);
  const { openModal, currentId } = useGetLocationData();
  const editStatusModalIsOpen = openModal === "EditStatus";

  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const { notifySuccess } = useNotify();

  const handleCloseModal = () =>
    setDataReplace("openModal", "", "currentId", "");

  const onCompleted = () => {
    notifySuccess(t("documents.notification.createSuccess"));
    handleCloseModal();
    reFetchTable?.();
  };

  const [getProfile, getProfileState] = useGetProfileLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getDocument, getDocumentState] = useGetDocumentLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [editStatus] = useSetDocumentStatusMutation({
    onCompleted,
  });

  useEffect(() => {
    if (editStatusModalIsOpen && !!currentId) {
      getProfile();
      getDocument({
        variables: {
          id: currentId,
        },
      });
    }
  }, [editStatusModalIsOpen, currentId]);

  // Берём документ из ответа только если он совпадает с открытым currentId,
  // иначе при повторном открытии модалки могут мелькнуть данные прошлого документа
  const loadedDocument = get(getDocumentState.data, "DocumentGetter.getOne");
  const documentInstance =
    editStatusModalIsOpen && !!currentId && loadedDocument?.id === currentId
      ? loadedDocument
      : undefined;

  const cantEdit =
    !!getProfileState.data && !!documentInstance
      ? userHaveAccessToDocumentEdit(
          getProfileState.data,
          documentInstance,
          hasPermission("documents.setStatus"),
        )
      : false;

  const onSubmit = (data: IDocumentChangeStatusForm) =>
    editStatus({
      variables: { ...dataToRequest_SetStatus(data), id: currentId },
    });

  return (
    <Modal
      open={editStatusModalIsOpen}
      title={t("documentLib.documentChangeStatus")}
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: documentStatusFormId,
        children: t("ui.save"),
      }}
    >
      {documentInstance && !getDocumentState.loading ? (
        <DocumentStatusForm
          formId={documentStatusFormId}
          cantEdit={cantEdit}
          defaultValues={{
            status: documentInstance.status as DocumentStatus,
            canceledReason: documentInstance.canceledReason,
          }}
          onSubmitHandler={onSubmit}
        />
      ) : (
        <Preloader />
      )}
    </Modal>
  );
};

export default DocumentStatusModal;
